import type React from 'react';

interface StatusBadgeProps {
  status: 'good' | 'neutral' | 'bad' | 'ahead' | 'upward' | 'maintaining' | 'slowing' | 'declining' | 'none';
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  className = '',
  size = 'md',
  showIcon = true
}) => {
  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  // Status configurations
  const statusConfig = {
    good: {
      label: 'Good',
      icon: '✅',
      classes: 'bg-green-100 text-green-800 border-green-200'
    },
    neutral: {
      label: 'Neutral',
      icon: '➖',
      classes: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    },
    bad: {
      label: 'Needs Work',
      icon: '⚠️',
      classes: 'bg-red-100 text-red-800 border-red-200'
    },
    ahead: {
      label: 'Ahead',
      icon: '🚀',
      classes: 'bg-green-100 text-green-800 border-green-200'
    },
    upward: {
      label: 'Improving',
      icon: '📈',
      classes: 'bg-green-100 text-green-800 border-green-200'
    },
    maintaining: {
      label: 'Steady',
      icon: '➡️',
      classes: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    slowing: {
      label: 'Slowing',
      icon: '⚠️',
      classes: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    },
    declining: {
      label: 'Declining',
      icon: '📉',
      classes: 'bg-red-100 text-red-800 border-red-200'
    },
    none: {
      label: 'No Data',
      icon: '📊',
      classes: 'bg-gray-100 text-gray-800 border-gray-200'
    }
  };

  const config = statusConfig[status] ?? statusConfig.none;

  return (
    <span
      className={`
        inline-flex items-center gap-1 font-medium rounded-full border
        ${sizeClasses[size]}
        ${config.classes}
        ${className}
      `}
    >
      {showIcon && <span className="leading-none">{config.icon}</span>}
      <span>{config.label}</span>
    </span>
  );
};